import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './wysupp-green-transparent_1.svg';
import { FaLock, FaCheckCircle, FaTimesCircle, FaEye, FaEyeSlash, FaShieldAlt } from 'react-icons/fa';
import { useAuth } from './AuthContext'; // <-- 1. Import useAuth
import axiosInstance from './api/axios'; // <-- 2. Import axiosInstance

export default function ChangePassword() {
    // --- STATE AND PROPS ---
    const { user, accessToken, requiresPasswordChange, loginUsername, logoutUser, isLoading } = useAuth();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    // Only asked for with no session (a first login or an expired password):
    // the old password alone is not enough to replace it.
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    // Password validation state
    const [lowerValidated, setLowerValidated] = useState(false);
    const [upperValidated, setUpperValidated] = useState(false);
    const [numberValidated, setNumberValidated] = useState(false);
    const [specialValidated, setSpecialValidated] = useState(false);
    const [lengthValidated, setLengthValidated] = useState(false);

    const navigate = useNavigate();
    const forcedChange = !accessToken && !!loginUsername;
    const allPasswordRequirementsMet = lowerValidated && upperValidated && numberValidated && specialValidated && lengthValidated;

    const passwordsDoNotMatch = newPassword && confirmNewPassword && newPassword !== confirmNewPassword;

    useEffect(() => {
        // Wait until the auth state is resolved
        if (!isLoading && !accessToken && !requiresPasswordChange) {
            // If we are not logged in AND not required to change password, redirect to login
            navigate('/');
        }
    }, [isLoading, accessToken, requiresPasswordChange, navigate]);

    // --- LOGIC AND HANDLERS ---
    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword !== confirmNewPassword) {
            setError('New passwords do not match.');
            return;
        }

        if (!allPasswordRequirementsMet) {
            setError('Please ensure the new password meets all requirements.');
            return;
        }

        if (forcedChange && !/^\d{6}$/.test(otp)) {
            setError('Enter the 6-digit code from your authenticator app.');
            return;
        }

        setLoading(true);

        try {
            // --- FIX APPLIED HERE: Conditionally add username to the payload ---
            const payload = {
                old_password: oldPassword,
                new_password: newPassword,
            };
    
            if (forcedChange) {
                // If we're in the "force change" state, include the username
                // and the 2FA code
                payload.username = loginUsername;
                payload.otp = otp;
            }
    
            await axiosInstance.put('/api/user/change-password', payload);
    
            setSuccess("Password changed successfully! You will be logged out for security.");
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            setTimeout(() => {
                logoutUser(); 
            }, 3000);
            
            setLoading(false); // Stop loading on success
        } catch (err) {
            // Every refusal is shown, 401s included: a wrong old password or 2FA
            // code answers 401, and swallowing it left the button stuck on
            // "Updating..." with no message. This page is outside the session
            // redirects, so a 401 here never means "you were logged out" on its own.
            const detail = err.response?.data?.detail;
            setError(
                typeof detail === 'string' ? detail
                : Array.isArray(detail) ? (detail[0]?.msg || 'Invalid input provided.')
                : 'An error occurred. Please try again.'
            );
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setLoading(false); // Stop loading on actual errors
        }
    };

    const handleNewPasswordChange = (value) => {
        setNewPassword(value);
        setLowerValidated(/(?=.*[a-z])/.test(value));
        setUpperValidated(/(?=.*[A-Z])/.test(value));
        setNumberValidated(/(?=.*[0-9])/.test(value));
        setSpecialValidated(/(?=.*[!@#$%^&*.])/.test(value));
        setLengthValidated(/.{12,}/.test(value));
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }
    
    // --- JSX (remains mostly the same) ---
    return (
        <div className="hero-section">
            <div className="change-password-form-wrapper">
                <img src={logo} alt="Wysupp Logo" className="form-logo" />
                <h1 className="form-title">Change Your Password</h1>
                <p className="form-subtitle">Update your password for user: <strong>{user?.username || loginUsername}</strong></p>
                <hr className="separator" />
                {error && <p className="form-errmsg">{error}</p>}
                {success && <p className="form-successmsg">{success}</p>}
                <form onSubmit={handleSubmit} className="change-password-form">
                <div className="input-group">
                        <FaLock className="input-icon" />
                        <input type={showOldPassword ? 'text' : 'password'} id="oldPassword" placeholder='Old Password' onChange={(e) => setOldPassword(e.target.value)} value={oldPassword} required />
                        {showOldPassword ? (
                            <FaEyeSlash className="password-toggle-icon" onClick={() => setShowOldPassword(false)} />
                        ) : (
                            <FaEye className="password-toggle-icon" onClick={() => setShowOldPassword(true)} />
                        )}
                    </div>
                    {forcedChange && (
                        <div className="input-group">
                            <FaShieldAlt className="input-icon" />
                            <input
                                type="text"
                                id="otp"
                                name="otp"
                                placeholder="6-digit code from your authenticator app"
                                value={otp}
                                /* Digits only: a pasted code often arrives as "123 456". */
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                maxLength="6"
                                autoComplete="one-time-code"
                                inputMode="numeric"
                                required
                            />
                        </div>
                    )}
                    {/* --- FIX: Updated new password input group --- */}
                    <div className="input-group">
                        <FaLock className="input-icon" />
                        <input type={showNewPassword ? 'text' : 'password'} id="newPassword" placeholder='New Password' onChange={(e) => handleNewPasswordChange(e.target.value)} value={newPassword} required />
                        {showNewPassword ? (
                            <FaEyeSlash className="password-toggle-icon" onClick={() => setShowNewPassword(false)} />
                        ) : (
                            <FaEye className="password-toggle-icon" onClick={() => setShowNewPassword(true)} />
                        )}
                    </div>
                    {/* --- FIX: Updated confirm password input group --- */}
                    <div className="input-group">
                        <FaLock className="input-icon" />
                        <input type={showConfirmNewPassword ? 'text' : 'password'} id="confirmNewPassword" placeholder='Confirm New Password' onChange={(e) => setConfirmNewPassword(e.target.value)} value={confirmNewPassword} required />
                        {showConfirmNewPassword ? (
                            <FaEyeSlash className="password-toggle-icon" onClick={() => setShowConfirmNewPassword(false)} />
                        ) : (
                            <FaEye className="password-toggle-icon" onClick={() => setShowConfirmNewPassword(true)} />
                        )}
                    </div>
                    <div className="password-tracker">
                        <p className={lowerValidated ? 'validated' : 'not-validated'}>{lowerValidated ? <FaCheckCircle /> : <FaTimesCircle />} At least one lowercase letter</p>
                        <p className={upperValidated ? 'validated' : 'not-validated'}>{upperValidated ? <FaCheckCircle /> : <FaTimesCircle />} At least one uppercase letter</p>
                        <p className={numberValidated ? 'validated' : 'not-validated'}>{numberValidated ? <FaCheckCircle /> : <FaTimesCircle />} At least one number</p>
                        <p className={specialValidated ? 'validated' : 'not-validated'}>{specialValidated ? <FaCheckCircle /> : <FaTimesCircle />} At least one special character (!@#$...)</p>
                        <p className={lengthValidated ? 'validated' : 'not-validated'}>{lengthValidated ? <FaCheckCircle /> : <FaTimesCircle />} At least 12 characters long</p>
                    </div>

                    {passwordsDoNotMatch && (
                        <p className="form-errmsg" style={{ marginTop: '1rem', marginBottom: '0' }}>
                            Passwords do not match.
                        </p>
                    )}

                    <hr className="separator" />
                    <button className="form-button" disabled={loading || !allPasswordRequirementsMet || newPassword !== confirmNewPassword}>
                        {loading ? 'Updating...' : 'Change Password'}
                    </button>
                </form>
                 <button onClick={() => navigate('/home')} className="back-link">Back to Home</button>
            </div>
            
            <style>{`
                :root {
                    --dark-bg: #1a202c;
                    --form-bg: #2d3748;
                    --page-bg: #1a202c;
                    --text-color: #e2e8f0;
                    --light-text: #a0aec0;
                    --accent-color: #48bb78;
                    --input-bg: #1a202c;
                    --border-color: #4a5568;
                    --error-color: #fca5a5;
                    --error-bg: rgba(239, 68, 68, 0.1);
                    --error-border: rgba(239, 68, 68, 0.3);
                    --success-color: #a7f3d0;
                    --success-bg: rgba(52, 211, 153, 0.1);
                    --success-border: rgba(52, 211, 153, 0.3);
                }

                .hero-section {
                    background: linear-gradient(135deg, #1f2937, #163029); /* Dark gradient background */
                    padding: 6rem 3rem;
                    text-align: center;
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 100px;
                    left: 50px;
                    width: 200px;
                    height: 200px;
                    background: radial-gradient(circle at center, rgba(72,187,120,0.3) 0%, transparent 70%);
                    transform: rotate(45deg);
                    z-index: 0;
                }

                .password-toggle-icon {
                    position: absolute;
                    top: 50%;
                    right: 1rem;
                    transform: translateY(-50%);
                    color: var(--light-text);
                    cursor: pointer;
                }

                .hero-section::after {
                    content: '';
                    position: absolute;
                    bottom: 100px;
                    right: 50px;
                    width: 200px;
                    height: 200px;
                    background: radial-gradient(circle at center, rgba(72,187,120,0.3) 0%, transparent 70%);
                    transform: rotate(-45deg);
                    z-index: 0;
                }

                .change-password-form-wrapper, .change-password-form-wrapper * {
                    box-sizing: border-box;
                }
                
                .change-password-form-wrapper {
                    width: 100%;
                    max-width: 450px;
                    background-color: var(--form-bg);
                    padding: 3rem;
                    border-radius: 8px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.3);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                
                .form-logo {
                    width: 350px;
                    margin-bottom: 1.5rem;
                }
                
                .form-title {
                    font-size: 2rem;
                    font-weight: 800;
                    color: var(--text-color);
                    margin-bottom: 0.5rem;
                    text-align: center;
                }

                .form-subtitle {
                    font-size: 1rem;
                    color: var(--light-text);
                    margin-bottom: 0rem;
                    text-align: center;
                }
                
                .separator {
                    width: 100%;
                    border: none;
                    border-top: 1px solid var(--border-color);
                    margin: 1.5rem 0; /* Adjust margin as needed */
                }

                .form-errmsg, .form-successmsg {
                    width: 100%;
                    padding: 0.75rem;
                    border-radius: 6px;
                    margin-bottom: 1.5rem;
                    font-size: 0.9rem;
                    text-align: center;
                    font-weight: 500;
                }

                .form-errmsg {
                    background-color: var(--error-bg);
                    color: var(--error-color);
                    border: 1px solid var(--error-border);
                }
                
                .form-successmsg {
                    background-color: var(--success-bg);
                    color: var(--success-color);
                    border: 1px solid var(--success-border);
                }

                .change-password-form {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    gap: 1.25rem;
                }
                
                .input-group {
                    position: relative;
                }
                
                .input-group input {
                    width: 100%;
                    height: 0rem;
                    padding: 1rem 1rem 1rem 3rem;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    color: var(--text-color);
                    font-size: 1rem;
                    transition: border-color 0.2s;
                }
                
                .input-group input:focus {
                    outline: none;
                    border-color: var(--accent-color);
                }
                
                .input-icon {
                    position: absolute;
                    top: 50%;
                    left: 1rem;
                    transform: translateY(-50%);
                    color: var(--light-text);
                }

                .password-tracker {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    margin: 0.5rem 0;
                    margin-bottom: 0rem;
                    font-size: 0.85rem;
                }
                
                .password-tracker p {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: color 0.2s;
                }
                
                .password-tracker .not-validated {
                    color: var(--light-text);
                }
                
                .password-tracker .validated {
                    color: var(--accent-color);
                    font-weight: 500;
                }
                
                .form-button {
                    background-color: var(--accent-color);
                    color: var(--page-bg);
                    border: none;
                    border-radius: 6px;
                    padding: 1rem;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }
                
                .form-button:hover:not(:disabled) {
                    background-color: #38a169;
                }
                
                .form-button:disabled {
                    background-color: #4a5568;
                    cursor: not-allowed;
                    opacity: 0.7;
                }
                
                .back-link {
                    background: none;
                    border: none;
                    color: var(--light-text);
                    font-size: 0.9rem;
                    margin-top: 1.5rem;
                    cursor: pointer;
                    transition: color 0.2s;
                }

                .input-group input:-webkit-autofill ~ .password-toggle-icon,
                .input-group input:autofill ~ .password-toggle-icon {
                color: var(--dark-bg); /* Change to a dark color */
                /* Or use a specific dark grey: color: #1a202c; */
                }

                /* Optional: Adjust hover color when autofilled */
                .input-group input:-webkit-autofill ~ .password-toggle-icon:hover,
                .input-group input:autofill ~ .password-toggle-icon:hover {
                color: var(--card-bg); /* Use a slightly lighter dark color on hover */
                /* Or use a specific dark grey: color: #2d3748; */
                }

                .back-link:hover {
                    color: var(--accent-color);
                }
            `}</style>
        </div>
    );
}